import { CSSProperties, useMemo, useState } from 'react';
import { flexRender } from '@tanstack/react-table';
import { useSortable, SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cn from 'classnames';
import * as ContextMenu from '@radix-ui/react-context-menu';

import { FilterProps, THProps, THeadProps } from './THead.props';
import styles from './THead.module.css';

const Filter = <T,>({ column }: FilterProps<T>) => (
  <input
    type='text'
    value={(column.getFilterValue() ?? '') as string}
    onChange={e => column.setFilterValue(e.target.value)}
    placeholder='Поиск...'
    className={styles.thFilterInput}
  />
);

const DraggableTableHeader = <T,>({
  table,
  header,
  enableColumnFilters,
}: THProps<T>) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: header.column.id,
    disabled: header.column.id === 'select',
  });

  const [hover, setHover] = useState(false);

  const isResizing = header.column.getIsResizing();

  const style: CSSProperties = {
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    transition: isResizing ? 'none' : 'width transform 0.2s ease-in-out',
    whiteSpace: 'nowrap',
    width: header.column.getSize(),
    zIndex: isDragging ? 1 : 0,
  };

  const onMouseEnterHandler = () => {
    setHover(true);
  };

  const onMouseLeaveHandler = () => {
    setHover(false);
  };

  const sorted = header.column.getIsSorted();

  return (
    <div ref={setNodeRef} className={styles.th} style={style}>
      <div
        {...{
          className: styles.thTitle,
          onMouseEnter: onMouseEnterHandler,
          onMouseLeave: onMouseLeaveHandler,
        }}
      >
        <div className={styles.fullContainer}>
          <div
            {...{
              className: header.column.getCanSort() ? styles.sortable : undefined,
              ...attributes,
              ...listeners,
              onClick: header.column.getCanSort()
                ? header.column.getToggleSortingHandler()
                : () => ({}),
            }}
          >
            {header.isPlaceholder ? null : (
              <>
                {header.column.getCanSort() && sorted ? (
                  <span
                    className={cn(styles.arrow, {
                      [styles.arrowAsc]: sorted === 'asc',
                      [styles.arrowDesc]: sorted === 'desc',
                    })}
                  />
                ) : null}
                {flexRender(header.column.columnDef.header, header.getContext())}
              </>
            )}
          </div>

          <div
            {...{
              onDoubleClick: () => header.column.resetSize(),
              onMouseDown: header.getResizeHandler(),
              onTouchStart: header.getResizeHandler(),
              className: cn(styles.resizer, {
                [styles.resizerActive]: header.column.getIsResizing() || hover,
              }),
            }}
          />
        </div>
      </div>

      {enableColumnFilters ? (
        <>
          {header.column.getCanFilter() ? (
            <div className={styles.thFilterBlock} onContextMenu={e => e.stopPropagation()}>
              <Filter column={header.column} table={table} />
            </div>
          ) : (
            <div className={styles.thFilterEmptyBlock} onContextMenu={e => e.stopPropagation()} />
          )}
        </>
      ) : null}
    </div>
  );
};

export default function THead<T>({
  tableId,
  table,
  columnOrder,
  enableColumnFilters,
  rowId,
}: THeadProps<T>) {
  const menuId = useMemo(
    () => (tableId ? `${tableId}-head-context-menu-${rowId}` : 'same_unique_identifier'),
    [rowId]
  );

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>
        <div className={styles.thead}>
          {table.getHeaderGroups().map(headerGroup => (
            <div key={headerGroup.id} className={styles.headerRow}>
              <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                {headerGroup.headers.map(header => (
                  <DraggableTableHeader
                    key={header.id}
                    table={table}
                    header={header}
                    enableColumnFilters={enableColumnFilters}
                  />
                ))}
              </SortableContext>
            </div>
          ))}
        </div>
      </ContextMenu.Trigger>

      <ContextMenu.Portal>
        <ContextMenu.Content className={styles.menu}>
          <ContextMenu.Item className={styles.menuItem}>
            <label htmlFor={`${menuId}-input-all`} className={styles.menuLabel}>
              <input
                {...{
                  id: `${menuId}-input-all`,
                  type: 'checkbox',
                  checked: table.getIsAllColumnsVisible(),
                  onChange: table.getToggleAllColumnsVisibilityHandler(),
                }}
              />{' '}
              Все
            </label>
          </ContextMenu.Item>
          {table
            .getAllLeafColumns()
            .filter(
              column => column?.columnDef?.header && typeof column?.columnDef?.header === 'string'
            )
            .map(column => (
              <ContextMenu.Item key={column.id} className={styles.menuItem}>
                <label htmlFor={`${menuId}-input-${column.id}`} className={styles.menuLabel}>
                  <input
                    {...{
                      id: `${menuId}-input-${column.id}`,
                      type: 'checkbox',
                      checked: column.getIsVisible(),
                      onChange: column.getToggleVisibilityHandler(),
                    }}
                  />{' '}
                  {column?.columnDef?.header?.toString()}
                </label>
              </ContextMenu.Item>
            ))}
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}
